import { useEffect, useRef, useState } from 'react';
import { IconMail, IconX, IconCheck } from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import authApi from '../../services/authApi';

const RESEND_SECONDS = 30;

/** 6-box numeric OTP input (mirrors the registration flow). */
function OtpBoxes({ value, onChange, onComplete, error, disabled }) {
    const refs = useRef([]);
    const digits = Array.from({ length: 6 }, (_, i) => value[i] || '');

    const emit = (next) => {
        onChange(next);
        if (next.length === 6 && /^\d{6}$/.test(next)) onComplete?.(next);
    };
    const handleChange = (i, e) => {
        const char = e.target.value.replace(/\D/g, '').slice(-1);
        const next = digits.map((d, idx) => (idx === i ? char : d)).join('');
        emit(next);
        if (char && i < 5) refs.current[i + 1]?.focus();
    };
    const handleKey = (i, e) => {
        if (e.key === 'Backspace') {
            if (!digits[i] && i > 0) refs.current[i - 1]?.focus();
            onChange(digits.map((d, idx) => (idx === i ? '' : d)).join(''));
        }
    };
    const handlePaste = (e) => {
        const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
        if (!pasted) return;
        e.preventDefault();
        emit(pasted);
        refs.current[Math.min(pasted.length, 5)]?.focus();
    };

    return (
        <div className="flex gap-2 justify-center" onPaste={handlePaste}>
            {digits.map((d, i) => (
                <input
                    key={i}
                    ref={(el) => (refs.current[i] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={d}
                    disabled={disabled}
                    autoFocus={i === 0}
                    onChange={(e) => handleChange(i, e)}
                    onKeyDown={(e) => handleKey(i, e)}
                    className={`w-10 h-12 sm:w-12 sm:h-14 text-center text-lg font-bold border-2 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/40 bg-white dark:bg-zinc-800 dark:text-zinc-100 transition-colors disabled:opacity-50 ${
                        error ? 'border-red-500' : d ? 'border-primary' : 'border-zinc-300 dark:border-zinc-600'
                    }`}
                />
            ))}
        </div>
    );
}

/**
 * EmailVerifyModal — sends a 6-digit code to `email` when opened, lets the user
 * enter it, and verifies via the profile email-OTP endpoints. On success the email
 * is saved + marked verified server-side; `onVerified(user, bonusGranted)` fires so
 * the caller can refresh state and celebrate the completion bonus.
 */
export default function EmailVerifyModal({ open, onOpenChange, email, onVerified }) {
    const { t } = useTranslation();
    const [otp, setOtp] = useState('');
    const [sending, setSending] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const [error, setError] = useState('');
    const [cooldown, setCooldown] = useState(0);

    const send = async () => {
        setSending(true);
        setError('');
        try {
            await authApi.sendProfileEmailOtp({ email });
            setCooldown(RESEND_SECONDS);
            notifications.show({ message: t('emailVerify.sent', { email }) || `${email} এ কোড পাঠানো হয়েছে`, color: 'green' });
        } catch (err) {
            const msg = err?.message || t('emailVerify.sendFailed') || 'কোড পাঠানো যায়নি';
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
        } finally {
            setSending(false);
        }
    };

    // Auto-send when the modal opens; reset state when it closes.
    useEffect(() => {
        if (open) { setOtp(''); setError(''); send(); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Resend cooldown ticker.
    useEffect(() => {
        if (cooldown <= 0) return;
        const id = setTimeout(() => setCooldown((s) => s - 1), 1000);
        return () => clearTimeout(id);
    }, [cooldown]);

    const verify = async (code) => {
        const value = code || otp;
        if (!/^\d{6}$/.test(value) || verifying) return;
        setVerifying(true);
        setError('');
        try {
            const res = await authApi.verifyProfileEmailOtp({ email, otp: value });
            notifications.show({ message: t('emailVerify.verified') || 'ইমেইল যাচাই সম্পন্ন হয়েছে', color: 'green' });
            onVerified?.(res?.data, res?.profileCompletionBonus === true);
            onOpenChange(false);
        } catch (err) {
            setOtp('');
            const msg = err?.message || t('emailVerify.invalidCode') || 'ভুল বা মেয়াদোত্তীর্ণ কোড';
            setError(msg);
        } finally {
            setVerifying(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent showClose={false} className="p-0 overflow-hidden" style={{ width: '440px', maxWidth: '95vw' }}>
                <div className="flex items-center justify-between gap-4 px-5 py-4 border-b border-zinc-100 dark:border-zinc-800">
                    <div className="flex items-center gap-2">
                        <IconMail size={18} className="text-primary" />
                        <DialogTitle className="text-base font-semibold text-zinc-800 dark:text-zinc-100">
                            {t('emailVerify.title') || 'ইমেইল যাচাই করুন'}
                        </DialogTitle>
                    </div>
                    <button
                        type="button"
                        onClick={() => onOpenChange(false)}
                        aria-label={t('common.close') || 'বন্ধ'}
                        className="inline-flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors"
                    >
                        <IconX size={18} />
                    </button>
                </div>

                <div className="p-5 space-y-4">
                    <p className="text-sm text-zinc-600 dark:text-zinc-400 text-center">
                        {t('emailVerify.subtitle') || 'নিচের ৬-সংখ্যার কোডটি এই ইমেইলে পাঠানো হয়েছে:'}
                        <br />
                        <span className="font-semibold text-zinc-800 dark:text-zinc-100 break-all">{email}</span>
                    </p>

                    <OtpBoxes value={otp} onChange={setOtp} onComplete={verify} error={!!error} disabled={sending || verifying} />

                    {error && <p className="text-xs text-red-500 text-center">{error}</p>}

                    <button
                        type="button"
                        onClick={() => verify()}
                        disabled={!/^\d{6}$/.test(otp) || verifying || sending}
                        className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 bg-primary hover:bg-primary/90 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {verifying
                            ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            : <IconCheck size={16} />}
                        {t('emailVerify.verify') || 'যাচাই করুন'}
                    </button>

                    <div className="text-center text-xs text-zinc-500 dark:text-zinc-400">
                        {cooldown > 0 ? (
                            <span>{t('emailVerify.resendIn', { sec: cooldown }) || `${cooldown} সেকেন্ড পর আবার পাঠান`}</span>
                        ) : (
                            <button
                                type="button"
                                onClick={send}
                                disabled={sending}
                                className="font-semibold text-primary hover:underline disabled:opacity-50"
                            >
                                {sending ? (t('emailVerify.sending') || 'পাঠানো হচ্ছে…') : (t('emailVerify.resend') || 'কোড আবার পাঠান')}
                            </button>
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
